import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaRobot, FaPaperPlane, FaTimes, FaCommentDots, FaExpand, FaCompress, FaRedo } from 'react-icons/fa';
import API from '../../config/axiosConfig';
import { fileService } from '../../services/FileService';
import { UserContext } from '../../context/UserContext';
import { AI_CHAT_ENABLED } from '../../services/FeatureFlagService';
import MarkdownMessage from './MarkdownMessage';

// Styles
const slideIn = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ChatWidget = styled.div<{ $isOpen: boolean; $isMinimized: boolean; $isExpanded: boolean; $isAddButtonVisible: boolean }>`
  position: fixed;
  ${props => props.$isExpanded ? css`
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(900px, 90vw);
    height: min(700px, 85vh);
    @media (max-width: 768px) {
      width: 95vw;
      height: 90vh;
    }
  ` : css`
    /* Desktop positioning */
    ${props.$isAddButtonVisible ? css`
      bottom: 120px;
      left: 20px;
    ` : css`
      bottom: 40px;
      left: 40px;
    `}

    /* Mobile positioning */
    @media (max-width: 768px) {
      bottom: 20px;
      ${props.$isAddButtonVisible ? css`
        left: 20px;
        right: 20px;
        width: auto;
      ` : css`
        left: 20px;
      `}
    }

    width: ${props.$isMinimized ? '200px' : '350px'};
    height: ${props.$isMinimized ? 'auto' : (props.$isOpen ? '500px' : '0')};
  `}
  background: white;
  border-radius: 12px;
  box-shadow: 0 5px 25px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  z-index: 9999;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-family: "Rubik", sans-serif;
  direction: rtl;
  overflow: hidden;

  ${props => props.$isOpen && !props.$isMinimized && !props.$isExpanded && css`
    animation: ${slideIn} 0.3s ease-out;
  `}
`;

const Header = styled.div`
  background: linear-gradient(135deg, #103C6E 0%, #0a2a4d 100%);
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 1rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
`;

const IconButton = styled.button<{ $hideOnMobile?: boolean }>`
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: white;
    background: rgba(255,255,255,0.1);
  }

  ${props => props.$hideOnMobile && css`
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

const MessagesArea = styled.div`
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.95rem;
  line-height: 1.5;
  position: relative;
  word-wrap: break-word;
  /* pre-wrap only for plain user text; bot messages use block markdown elements */
  white-space: ${props => props.$isUser ? 'pre-wrap' : 'normal'};

  ${props => props.$isUser ? css`
    background: #103C6E;
    color: white;
    align-self: flex-start;
    border-top-right-radius: 2px;
  ` : css`
    background: white;
    color: #333;
    align-self: flex-end;
    border-top-left-radius: 2px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  `}
`;

const CitationList = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0,0,0,0.1);
  font-size: 0.85rem;
`;

const CitationItem = styled.div`
  margin-bottom: 8px;
`;

const CitationLink = styled.a`
  color: #103C6E;
  text-decoration: none;
  margin-left: 8px;
  &:hover {
    text-decoration: underline;
  }
`;

const InputArea = styled.div`
  padding: 12px;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ScopeSelector = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ScopeSelect = styled.select`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 6px 8px;
  font-family: "Rubik", sans-serif;
  font-size: 0.85rem;
  color: #333;
  background: white;
  outline: none;
  direction: rtl;

  &:focus {
    border-color: #103C6E;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const TextArea = styled.textarea`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 12px;
  resize: none;
  min-height: 40px;
  max-height: 100px;
  font-family: "Rubik", sans-serif;
  outline: none;
  font-size: 0.95rem;

  &:focus {
    border-color: #103C6E;
  }
`;

const SendButton = styled.button`
  background: #103C6E;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #0a2a4d;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  justify-content: center;

  div {
    width: 6px;
    height: 6px;
    background: #ccc;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const ToggleButton = styled.button<{ $isAddButtonVisible: boolean }>`
  position: fixed;
  /* Desktop positioning */
  ${props => props.$isAddButtonVisible ? css`
    bottom: 120px;
    left: 20px;
  ` : css`
    bottom: 40px;
    left: 40px;
  `}

  /* Mobile positioning */
  @media (max-width: 768px) {
    bottom: 20px;
    ${props => props.$isAddButtonVisible ? css`
      left: 90px;
    ` : css`
      left: 20px;
    `}
  }

  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #103C6E;
  color: white;
  border: none;
  box-shadow: 0 4px 15px rgba(16, 60, 110, 0.4);
  cursor: pointer;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

// One entry per FILE the answer cited, not per protocol — a protocol can be
// several files and the answer says which one it read. `id` matches the [n]
// marker left inline in the answer text.
type Citation = {
    id: number;
    title: string;
    fileName?: string;
    protocolId: string;
    filePath: string;
};

type Message = {
    id: string;
    type: 'user' | 'bot';
    text: string;
    citations?: Citation[];
    // Client-side failures (network / permission). Flagged so they are never
    // sent back as conversational context — the server would otherwise route
    // the next question against an error string. See
    // apps/server/src/services/ai/AiChat.md.
    isError?: boolean;
};

// Header title and opening bubble. Kept as constants because the server-side
// context builder drops the opening bubble by role, not by text — these can be
// reworded freely without touching the server.
const CHAT_TITLE = 'במה אפשר לעזור?';
const WELCOME_MESSAGE =
    'ניתן לשאול אותי שאלות. אענה רק מתוך הידע המצוי בפרוטוקולים. יש לוודא את נכונות התשובה ולא לקבל אותה כתורה למשה מסיני!';
const NO_SCOPE_WELCOME_MESSAGE = 'שלום! יש לבחור בית חולים ומחלקה כדי להתחיל.';

// Component to handle citation rendering with signed URLs
const CitationComponent: React.FC<{ citation: Citation }> = ({ citation }) => {
    const [signedUrl, setSignedUrl] = useState<string | null>(null);

    useEffect(() => {
        const getSignedUrl = async () => {
            if (citation.filePath) {
                try {
                    const signed = await fileService.getSignedUrl(citation.filePath);
                    setSignedUrl(signed);
                } catch (error) {
                    console.error('Error signing URL:', error);
                }
            }
        };

        getSignedUrl();
    }, [citation.filePath]);

    // The file name is only worth showing when it adds information — for a
    // single-file protocol it usually just repeats the protocol name.
    const showFileName = !!citation.fileName && citation.fileName !== citation.title;

    return (
        <CitationItem>
            <span>[{citation.id}] </span>
            {citation.protocolId && (
                <CitationLink href={`/protocols/${citation.protocolId}`} target="_blank" rel="noopener noreferrer">
                    {citation.title}
                </CitationLink>
            )}
            {signedUrl && citation.filePath && (
                <CitationLink href={signedUrl} target="_blank" rel="noopener noreferrer">
                    {showFileName ? `(${citation.fileName})` : '(קובץ)'}
                </CitationLink>
            )}
        </CitationItem>
    );
};

type ProtocolsAiChatProps = {
    isAddButtonVisible?: boolean;
    // Whether the current user's scope has at least one protocol to query.
    // The widget renders nothing when this is false — there is nothing to ask about.
    hasProtocols?: boolean;
};

const ProtocolsAiChat: React.FC<ProtocolsAiChatProps> = ({
    isAddButtonVisible = false,
    hasProtocols = true,
}) => {
    const { user, hospitals, isFeatureEnabled } = useContext(UserContext);

    // Check if chatbot should be visible
    const shouldShowChatbot = React.useMemo(() => {
        const hasFeatureAccess = isFeatureEnabled('AI_CHATBOT');
        if (hasFeatureAccess) {
            return true;
        }
        // Fallback to query param or localhost for testing
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has('enable-ai-chatbox');
    }, [isFeatureEnabled]);

    const isSuperAdmin: boolean = !!(user?.isSuperAdmin);
    const hasAnyRole: boolean = (user?.roles?.length ?? 0) > 0;

    // Super-admin scope selection
    const [selectedHospitalId, setSelectedHospitalId] = useState<string>('');
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');

    // Derive selected hospital object for department list
    const selectedHospital = React.useMemo(
        () => hospitals.find((h: { _id: string }) => h._id === selectedHospitalId) ?? null,
        [hospitals, selectedHospitalId]
    );

    // Input is enabled when:
    // - regular user with at least one role, OR
    // - super-admin who has selected both hospital and department
    const hasScope: boolean = isSuperAdmin
        ? !!(selectedHospitalId && selectedDepartmentId)
        : hasAnyRole;

    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Reset conversation when super-admin changes scope
    useEffect(() => {
        setMessages([]);
    }, [selectedHospitalId, selectedDepartmentId]);

    // When hospital selection changes, reset department selection
    const handleHospitalChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedHospitalId(e.target.value);
        setSelectedDepartmentId('');
    }, []);

    const handleDepartmentChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedDepartmentId(e.target.value);
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen, isMinimized]);

    // Don't render anything if the chatbot shouldn't be visible, or if there
    // are no protocols in scope to ask questions about.
    // NOTE: this guard must stay below every hook call above it (rules of hooks) —
    // `hasProtocols`/`shouldShowChatbot` can change between renders, so any hook
    // placed after this line would be called a different number of times.
    if (!AI_CHAT_ENABLED || !shouldShowChatbot || !hasProtocols) {
        return null;
    }

    // Reachable only for super-admins who haven't picked a hospital/department yet —
    // regular users with no protocols in scope never render this widget at all.
    const noScopePlaceholder = 'יש לבחור בית חולים ומחלקה כדי לשאול שאלות...';

    const handleSend = async () => {
        if (!input.trim() || loading || !hasScope) return;

        const userMsg: Message = { id: Date.now().toString(), type: 'user', text: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            // PREVIOUS turns only — the current question travels in `question`.
            // Failed turns are excluded so server-side error copy never becomes
            // context for the next question.
            const conversationHistory = messages
                .filter(msg => !msg.isError)
                .map(msg => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.text
                }));

            // Super-admin must send chosen scope; regular users let the server derive scope from JWT
            const requestBody: Record<string, unknown> = {
                question: userMsg.text,
                conversationHistory,
            };

            if (isSuperAdmin) {
                requestBody.hospitalId = selectedHospitalId;
                requestBody.departmentId = selectedDepartmentId;
            }

            const response = await API.post('/api/protocol-wiki/chat', requestBody);

            const botMsg: Message = {
                id: (Date.now() + 1).toString(),
                type: 'bot',
                text: response.data.answer,
                citations: response.data.citations,
            };
            setMessages(prev => [...prev, botMsg]);
        } catch (error: unknown) {
            const apiError = error as { response?: { status?: number } };
            console.error('Chat error:', error);

            const status = apiError.response?.status;
            let errorMessage = 'משהו השתבש בעיבוד הבקשה. אפשר לנסות שוב בעוד רגע.';
            if (status === 400) {
                errorMessage = 'יש לבחור בית חולים ומחלקה כדי שאוכל לחפש בפרוטוקולים.';
            } else if (status === 403) {
                errorMessage = 'אין לך הרשאה לגשת לפרוטוקולים של מחלקה זו.';
            }

            setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                type: 'bot',
                text: errorMessage,
                isError: true
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) {
        return (
            <ToggleButton onClick={() => setIsOpen(true)} title="AI Chat" $isAddButtonVisible={isAddButtonVisible}>
                <FaCommentDots />
            </ToggleButton>
        );
    }

    if (isMinimized) {
        return (
            <ChatWidget $isOpen={true} $isMinimized={true} $isExpanded={false} $isAddButtonVisible={isAddButtonVisible}>
                <Header onClick={() => setIsMinimized(false)}>
                    <HeaderTitle><FaRobot /> {CHAT_TITLE}</HeaderTitle>
                    <Controls>
                        <IconButton onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }} title="Expand">
                            <span style={{ fontSize: '14px' }}>◻</span>
                        </IconButton>
                        <IconButton onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} title="Close">
                            <FaTimes />
                        </IconButton>
                    </Controls>
                </Header>
            </ChatWidget>
        );
    }

    const welcomeMessage = hasScope ? WELCOME_MESSAGE : NO_SCOPE_WELCOME_MESSAGE;

    return (
        <ChatWidget $isOpen={isOpen} $isMinimized={false} $isExpanded={isExpanded} $isAddButtonVisible={isAddButtonVisible}>
            <Header onClick={() => !isExpanded && setIsMinimized(true)}>
                <HeaderTitle><FaRobot /> {CHAT_TITLE}</HeaderTitle>
                <Controls>
                    {/* Switching topic mid-chat works (the server detects it and
                        drops the stale context), but an explicit reset is the
                        clearest way for the user to start over. */}
                    {messages.length > 0 && (
                        <IconButton
                            onClick={(e) => { e.stopPropagation(); setMessages([]); }}
                            title="שיחה חדשה"
                            aria-label="שיחה חדשה"
                        >
                            <FaRedo />
                        </IconButton>
                    )}
                    <IconButton $hideOnMobile onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} title={isExpanded ? "Collapse" : "Expand"}>
                        {isExpanded ? <FaCompress /> : <FaExpand />}
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} title="Close">
                        <FaTimes />
                    </IconButton>
                </Controls>
            </Header>

            <MessagesArea>
                <MessageBubble key="welcome" $isUser={false}>
                    {welcomeMessage}
                </MessageBubble>
                {messages.map(msg => (
                    <MessageBubble key={msg.id} $isUser={msg.type === 'user'}>
                        {msg.type === 'user'
                            ? msg.text
                            : <MarkdownMessage text={msg.text} />
                        }
                        {msg.citations && msg.citations.length > 0 && (
                            <CitationList>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>מקורות:</div>
                                {msg.citations.map(cit => (
                                    <CitationComponent key={cit.id} citation={cit} />
                                ))}
                            </CitationList>
                        )}
                    </MessageBubble>
                ))}
                {loading && (
                    <MessageBubble $isUser={false}>
                        <LoadingDots><div></div><div></div><div></div></LoadingDots>
                    </MessageBubble>
                )}
                <div ref={messagesEndRef} />
            </MessagesArea>

            <InputArea>
                {isSuperAdmin && (
                    <ScopeSelector>
                        <ScopeSelect
                            value={selectedHospitalId}
                            onChange={handleHospitalChange}
                            aria-label="בחר בית חולים"
                        >
                            <option value="">-- בחר בית חולים --</option>
                            {hospitals.map((h: { _id: string; nameHebrew: string }) => (
                                <option key={h._id} value={h._id}>{h.nameHebrew}</option>
                            ))}
                        </ScopeSelect>
                        <ScopeSelect
                            value={selectedDepartmentId}
                            onChange={handleDepartmentChange}
                            disabled={!selectedHospitalId}
                            aria-label="בחר מחלקה"
                        >
                            <option value="">-- בחר מחלקה --</option>
                            {selectedHospital?.departments?.map((d: { _id: string; nameHebrew: string }) => (
                                <option key={d._id} value={d._id}>{d.nameHebrew}</option>
                            ))}
                        </ScopeSelect>
                    </ScopeSelector>
                )}
                <InputRow>
                    <TextArea
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={hasScope ? 'שאל/י שאלה על הפרוטוקולים...' : noScopePlaceholder}
                        disabled={loading || !hasScope}
                    />
                    <SendButton onClick={handleSend} disabled={loading || !input.trim() || !hasScope}>
                        {loading ? '...' : <FaPaperPlane />}
                    </SendButton>
                </InputRow>
            </InputArea>
        </ChatWidget>
    );
};

export default ProtocolsAiChat;
